import { useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import {
  Alert,
  Box,
  Card,
  CardContent,
  CircularProgress,
  Container,
  Grid,
  Typography,
} from '@mui/material';
import StarIcon from '@mui/icons-material/Star';
import { useAuthStore } from '../../stores/root_store';
import { metricsApi, type MyStats, type RatingSummary } from '../../api/metrics_api';

const StatCard = ({ label, children, caption }: {
  label: string;
  children: React.ReactNode;
  caption?: React.ReactNode;
}) => (
  <Card sx={{ height: '100%' }}>
    <CardContent>
      <Typography variant="overline" color="text.secondary">
        {label}
      </Typography>
      <Box sx={{ my: 1 }}>{children}</Box>
      {caption && (
        <Typography variant="body2" color="text.secondary">
          {caption}
        </Typography>
      )}
    </CardContent>
  </Card>
);

const RatingCard = ({ label, summary, minRatings }: {
  label: string;
  summary: RatingSummary;
  minRatings: number;
}) => {
  const ratings = `${summary.count} rating${summary.count === 1 ? '' : 's'}`;
  if (summary.average === null) {
    return (
      <StatCard label={label} caption={`Shown after ${minRatings} ratings`}>
        <Typography variant="h6" color="text.secondary">
          Not enough ratings yet
        </Typography>
      </StatCard>
    );
  }
  return (
    <StatCard label={label} caption={ratings}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Typography variant="h3" component="span">
          {summary.average.toFixed(2)}
        </Typography>
        <StarIcon sx={{ color: 'warning.main', fontSize: 32 }} />
      </Box>
    </StatCard>
  );
};

export const AssistantStatsPage = observer(() => {
  const authStore = useAuthStore();
  const [stats, setStats] = useState<MyStats | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    metricsApi.getMyStats()
      .then(setStats)
      .catch((err) => setError(err instanceof Error ? err.message : 'Failed to load stats'));
  }, []);

  const firstName = authStore.user?.first_name;
  const title = firstName ? `${firstName}'s stats` : 'Stats';

  const asOf = stats && new Date(stats.ratings_as_of).toLocaleDateString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC',
  });

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Typography variant="h4" gutterBottom>
        {title}
      </Typography>

      {error && <Alert severity="error">{error}</Alert>}

      {!stats && !error && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      )}

      {stats && (
        <>
          <Grid container spacing={3}>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <RatingCard
                label={`Avg rating · ${stats.window_days} days`}
                summary={stats.rating_30d}
                minRatings={stats.min_ratings_to_show}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <RatingCard
                label="Avg rating · all time"
                summary={stats.rating_all_time}
                minRatings={stats.min_ratings_to_show}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <StatCard label={`Tasks closed · ${stats.window_days} days`}>
                <Typography variant="h3">{stats.tasks_closed_30d}</Typography>
              </StatCard>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <StatCard label={`Mentor reviews · ${stats.window_days} days`}>
                <Box sx={{ display: 'flex', gap: 3 }}>
                  <Box>
                    <Typography variant="h3" color="success.main">{stats.mentor_30d.approvals}</Typography>
                    <Typography variant="body2" color="text.secondary">approved</Typography>
                  </Box>
                  <Box>
                    <Typography variant="h3" color="error.main">{stats.mentor_30d.denials}</Typography>
                    <Typography variant="body2" color="text.secondary">denied</Typography>
                  </Box>
                </Box>
              </StatCard>
            </Grid>
          </Grid>

          <Typography variant="body2" color="text.secondary" sx={{ mt: 3 }}>
            Ratings are updated weekly and include ratings received before {asOf}.
            Tasks and mentor reviews are up to date.
          </Typography>
        </>
      )}
    </Container>
  );
});
